import { Head, Link, router } from "@inertiajs/react"
import { Camera, Check, Clock, Download, Printer, Video, X } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  manager_name: string | null
}

interface EntryRevision {
  id: number
  entry_type: number
  entry_text: string | null
  user_name: string | null
  created_at: string | null
  can_delete: boolean
}

interface Entry {
  id: number
  entry_type: number
  source_id: number | null
  start_time: string | null
  entry_text: string | null
  user_id: number | null
  user_name: string | null
  also_names: string[]
  also_user_ids: number[]
  has_photo: boolean
  has_video: boolean
  status: number
  can_delete: boolean
  revisions: EntryRevision[]
}

interface Member {
  id: number
  user_id: number | null
  user_name: string | null
  approved_at: string | null
}

interface Rejection {
  id: number
  reason: string | null
  rejected_at: string | null
  rejected_by_name: string | null
}

interface ReportDetail {
  id: number
  report_type: number
  category: number
  report_date: string | null
  notes: string | null
  admin_notes: string | null
  significant: number
  investigator_name: string | null
  user_name: string | null
  user_id: number | null
  status: number
  vendor_id: number | null
  completed_by_name: string | null
  completed_at: string | null
  approved_by_name: string | null
  approved_at: string | null
  approved_text: string | null
  prepared_by_name: string | null
  prepared_at: string | null
  reviewed_by_name: string | null
  reviewed_at: string | null
  rejected_notes: string | null
  log: string | null
  used_in_final: number
  can_review: boolean
  can_approve: boolean
  can_reject: boolean
  can_prepare: boolean
  can_submit: boolean
  can_member_submit: boolean
  can_reject_member: boolean
  can_submit_all_members: boolean
  can_update: boolean
  can_add_entries: boolean
  can_destroy: boolean
  current_user_id: number | null
  is_team_leader: boolean
  current_user_member_approved: boolean
  all_members_approved: boolean
  entries: Entry[]
  members: Member[]
  rejections: Rejection[]
}

interface Props {
  case_record: CaseRecord
  report: ReportDetail
  can_download_pdf: boolean
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr)
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
}

function formatReportDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
}

const STATUS_LABELS: Record<number, string> = {
  0: "Draft",
  1: "Submitted",
  2: "Approved",
  3: "Prepared",
}

const STATUS_STYLES: Record<number, string> = {
  0: "bg-hpi-pale-gray text-hpi-dark-gray",
  1: "bg-hpi-yellow/30 text-hpi-dark-gray",
  2: "bg-hpi-dark-green/20 text-hpi-dark-green",
  3: "bg-hpi-light-blue text-hpi-dark-blue",
}

const CATEGORY_LABELS: Record<number, string> = {
  0: "Daily",
  1: "GPS",
  3: "Incident",
}

const ENTRY_TYPE_ORIGINAL = 0
const ENTRY_TYPE_REVISION = 1
const ENTRY_TYPE_FINAL = 10

function printEntryText(entry: Entry): string {
  let text = entry.entry_text ?? ""
  if (entry.user_name) {
    const names = [entry.user_name, ...entry.also_names]
    text += ` (${names.join("/")})`
  }
  return text
}

function hasAnyMedia(entry: Entry): boolean {
  return entry.has_photo || entry.has_video
}

function MediaIcons({ entry }: { entry: Entry }) {
  return (
    <>
      {entry.has_photo && <Camera className="inline-block h-4 w-4" />}
      {entry.has_video && <Video className="inline-block h-4 w-4" />}
    </>
  )
}

export default function Show({ case_record, report, can_download_pdf }: Props) {
  const [rejectReason, setRejectReason] = useState("")
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [showFinalizeForm, setShowFinalizeForm] = useState(false)
  const [finalizeInitials, setFinalizeInitials] = useState("")
  const [memberRejectId, setMemberRejectId] = useState<number | null>(null)
  const [memberRejectReason, setMemberRejectReason] = useState("")
  const [viewMode, setViewMode] = useState<"original" | "final" | "all">(
    "original",
  )

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: `Report #${report.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/reports`

  const investigators = [
    report.investigator_name,
    ...report.members.map((m) => m.user_name),
  ].filter(Boolean)

  const isPrepared = !!report.prepared_at

  // Current user is a team member (not the team leader)
  const isTeamMember = report.members.some(
    (m) => m.user_id === report.current_user_id,
  )

  const canToggleAlso = (entry: Entry): boolean => {
    if (report.status !== 0) return false
    if (entry.user_id === report.current_user_id) return false
    const isLeader = report.is_team_leader
    const isMember = report.members.some(
      (m) => m.user_id === report.current_user_id,
    )
    if (!isLeader && !isMember) return false
    if (isMember && report.current_user_member_approved) return false
    return true
  }

  const handleToggleAlso = (entryId: number) => {
    router.post(
      `${basePath}/${report.id}/entries/${entryId}/toggle_also`,
      {},
      { preserveScroll: true },
    )
  }

  // All three modes show originals at top level, except "final" which shows type=10
  const filteredEntries =
    viewMode === "final"
      ? report.entries.filter((e) => e.entry_type === ENTRY_TYPE_FINAL)
      : report.entries.filter((e) => e.entry_type === ENTRY_TYPE_ORIGINAL)

  // In "original" mode, nested revisions show only type=1. In "all" mode, show type=1 and type=10.
  const filterNestedRevisions = (revisions: EntryRevision[]) =>
    viewMode === "original"
      ? revisions.filter((r) => r.entry_type === ENTRY_TYPE_REVISION)
      : revisions

  // Print view only shows Final entries (matching legacy behavior)
  const printEntries = report.entries.filter(
    (e) => e.entry_type === ENTRY_TYPE_FINAL,
  )

  const printHaveMedia = printEntries.some(hasAnyMedia)
  const printHavePhoto = printEntries.some((e) => e.has_photo)
  const printHaveVideo = printEntries.some((e) => e.has_video)

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this report?")) {
      router.delete(`${basePath}/${report.id}`)
    }
  }

  const handleFinalize = () => {
    const approvalText = `I, ${finalizeInitials}, approve and confirm that the entries in this report are accurate and complete.`
    router.patch(`${basePath}/${report.id}/submit`, {
      approved_text: approvalText,
    })
  }

  const handleMemberApprove = () => {
    if (
      confirm(
        "Approve your entries? You will not be able to edit them after approval.",
      )
    ) {
      router.patch(`${basePath}/${report.id}/member_submit`)
    }
  }

  const handleSubmitAllMembers = () => {
    if (confirm("Approve all members' entries and finalize the report?")) {
      router.patch(`${basePath}/${report.id}/submit_all_members`)
    }
  }

  const handleReview = () => {
    router.patch(`${basePath}/${report.id}/review`)
  }

  const handleApprove = () => {
    if (confirm("Approve this report?")) {
      router.patch(`${basePath}/${report.id}/approve`)
    }
  }

  const handleReject = () => {
    router.patch(`${basePath}/${report.id}/reject`, {
      reason: rejectReason,
    })
  }

  const handleRejectMember = (memberId: number) => {
    router.patch(`${basePath}/${report.id}/reject_member`, {
      member_id: memberId,
      reason: memberRejectReason,
    })
    setMemberRejectId(null)
    setMemberRejectReason("")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Report - ${case_record.case_number}`} />

      {/* ── Print-only layout (hidden on screen) ── */}
      <div className="hidden print:block">
        <table className="mx-auto w-[740px] border-collapse text-left">
          {/* thead repeats on each printed page */}
          <thead>
            <tr>
              <td>
                <div className="border-b-2 border-dotted border-gray-600 pb-4 text-center">
                  <div className="text-sm leading-tight">
                    Hawk Professional Investigations
                  </div>
                  <div className="font-mono text-4xl leading-tight font-bold">
                    CONFIDENTIAL
                  </div>
                  <div className="text-lg font-bold">Surveillance Report</div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-left text-sm">
                    <div>
                      <div>
                        <strong>Date:</strong>{" "}
                        {formatReportDate(report.report_date)}
                      </div>
                      <div>
                        <strong>Case Name:</strong> {case_record.display_name}
                      </div>
                      {investigators.length > 0 && (
                        <div className="mt-2">
                          <strong>Investigators:</strong>
                          <div>{investigators.join(", ")}</div>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div>
                        <strong>Team Leader:</strong>{" "}
                        {report.investigator_name ?? "-"}
                      </div>
                      <div>
                        <strong>Supervisor:</strong>{" "}
                        {case_record.manager_name ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {/* Print entries — Finals only (matching legacy) */}
                {printEntries.length > 0 && (
                  <div className="pt-4">
                    <ul className="list-none space-y-4">
                      {printEntries.map((entry) => (
                        <li
                          key={entry.id}
                          className="border-b border-gray-300 pb-4"
                        >
                          <div className="font-bold">
                            {formatTime(entry.start_time)}
                          </div>
                          <div className="pl-4">
                            {hasAnyMedia(entry) && (
                              <span className="float-right ml-4 flex gap-1">
                                <MediaIcons entry={entry} />
                              </span>
                            )}
                            <span>{printEntryText(entry)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {printHaveMedia && (
                      <div className="mt-4 text-center text-sm">
                        Legend:{" "}
                        {printHavePhoto && (
                          <span className="ml-4 inline-flex items-center gap-1">
                            <Camera className="text-hpi-dark-green inline-block h-4 w-4" />{" "}
                            Photo
                          </span>
                        )}
                        {printHaveVideo && (
                          <span className="ml-4 inline-flex items-center gap-1">
                            <Video className="text-hpi-dark-green inline-block h-4 w-4" />{" "}
                            Video
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Screen-only content (hidden when printing) ── */}
      <div className="p-6 print:hidden">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {CATEGORY_LABELS[report.category] || "Report"} Report
            </h1>
            <span
              className={`rounded px-2 py-1 text-sm ${STATUS_STYLES[report.status] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
            >
              {STATUS_LABELS[report.status] || "Unknown"}
            </span>
            {report.significant === 1 && (
              <span className="rounded bg-purple-100 px-2 py-1 text-sm text-purple-700">
                Significant
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {can_download_pdf && (
              <a
                href={`/cases/${case_record.id}/report_downloads/daily_report_pdf/${report.id}`}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue flex items-center gap-1.5 rounded border px-4 py-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            )}
            <button
              onClick={() => window.print()}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue flex items-center gap-1.5 rounded border px-4 py-2"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            {report.can_add_entries && (
              <Link
                href={`${basePath}/${report.id}/entries/new`}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Add Entry
              </Link>
            )}
            {report.can_update && (
              <Link
                href={`${basePath}/${report.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
            {/* GAP 5: Team member "Approve" button */}
            {report.can_member_submit &&
              !report.current_user_member_approved && (
                <button
                  onClick={handleMemberApprove}
                  className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 flex items-center gap-1.5 rounded px-4 py-2 text-white"
                >
                  <Check className="h-4 w-4" />
                  Approve My Entries
                </button>
              )}
            {/* GAP 4: Submit all members at once */}
            {report.can_submit_all_members && (
              <button
                onClick={handleSubmitAllMembers}
                className="bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80 rounded px-4 py-2"
              >
                Approve All &amp; Finalize
              </button>
            )}
            {/* GAP 1 & 8: Finalize button with initials — only when all members approved */}
            {report.status === 0 && report.is_team_leader && (
              <button
                onClick={() => {
                  if (report.can_submit) {
                    setShowFinalizeForm(!showFinalizeForm)
                  }
                }}
                disabled={!report.all_members_approved}
                className={`rounded px-4 py-2 ${
                  report.all_members_approved
                    ? "bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80"
                    : "cursor-not-allowed bg-gray-200 text-gray-400"
                }`}
                title={
                  !report.all_members_approved
                    ? "All team members must approve their entries before you can finalize"
                    : "Finalize this report"
                }
              >
                Finalize
              </button>
            )}
            {report.can_review && (
              <button
                onClick={() => {
                  setShowReviewForm(!showReviewForm)
                  setShowRejectForm(false)
                  setShowFinalizeForm(false)
                }}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Pre-Approve
              </button>
            )}
            {report.can_approve && (
              <button
                onClick={handleApprove}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Approve
              </button>
            )}
            {report.can_reject && (
              <button
                onClick={() => {
                  setShowRejectForm(!showRejectForm)
                  setShowReviewForm(false)
                  setShowFinalizeForm(false)
                }}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Reject
              </button>
            )}
            {report.can_prepare && (
              <Link
                href={`${basePath}/${report.id}/prepare`}
                className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
              >
                Prepare Report
              </Link>
            )}
            {report.can_destroy && (
              <button
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {/* GAP 8: Finalize confirmation with initials */}
        {showFinalizeForm && (
          <div className="border-hpi-yellow/50 bg-hpi-yellow/10 mb-4 rounded border p-4">
            <h3 className="text-hpi-dark-gray mb-2 font-semibold">
              Finalize Report
            </h3>
            <p className="mb-3 text-sm">
              I approve and confirm that the entries in this report are accurate
              and complete. Enter your initials to confirm:
            </p>
            <input
              type="text"
              value={finalizeInitials}
              onChange={(e) => setFinalizeInitials(e.target.value)}
              className="border-hpi-light-gray mb-3 w-32 rounded border px-3 py-2"
              placeholder="Your initials"
              maxLength={5}
            />
            <div className="flex gap-2">
              <button
                onClick={handleFinalize}
                disabled={!finalizeInitials.trim()}
                className={`rounded px-4 py-2 ${
                  finalizeInitials.trim()
                    ? "bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80"
                    : "cursor-not-allowed bg-gray-200 text-gray-400"
                }`}
              >
                Finalize Report
              </button>
              <button
                onClick={() => setShowFinalizeForm(false)}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showReviewForm && (
          <div className="border-hpi-dark-green/30 bg-hpi-dark-green/5 mb-4 rounded border p-4">
            <h3 className="text-hpi-dark-green mb-2 font-semibold">
              Pre-Approve Report
            </h3>
            <p className="mb-3">
              I have read and reviewed this report and don&apos;t see any
              problems.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleReview}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Pre-Approve
              </button>
              <button
                onClick={() => setShowReviewForm(false)}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showRejectForm && (
          <div className="border-hpi-red/30 bg-hpi-red/5 mb-4 rounded border p-4">
            <h3 className="text-hpi-red mb-2 font-semibold">Reject Report</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="border-hpi-light-gray mb-2 w-full rounded border px-3 py-2"
              rows={3}
              placeholder="Reason for rejection..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => setShowRejectForm(false)}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Member approval status banner */}
        {report.status === 0 &&
          report.members.length > 0 &&
          report.is_team_leader && (
            <div
              className={`mb-4 rounded border p-3 text-sm ${
                report.all_members_approved
                  ? "border-hpi-dark-green/30 bg-hpi-dark-green/5 text-hpi-dark-green"
                  : "border-hpi-yellow/50 bg-hpi-yellow/10 text-hpi-dark-gray"
              }`}
            >
              {report.all_members_approved ? (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  All team members have approved their entries. You may now
                  finalize the report.
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Waiting for{" "}
                  {report.members.filter((m) => !m.approved_at).length}{" "}
                  member(s) to approve their entries before you can finalize.
                </span>
              )}
            </div>
          )}

        {/* Current user approved banner */}
        {isTeamMember &&
          report.current_user_member_approved &&
          report.status === 0 && (
            <div className="border-hpi-dark-green/30 bg-hpi-dark-green/5 text-hpi-dark-green mb-4 rounded border p-3 text-sm">
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                You have approved your entries. They are now locked for editing.
              </span>
            </div>
          )}

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            {report.notes && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">Notes</h2>
                <div className="whitespace-pre-wrap">{report.notes}</div>
              </div>
            )}

            {report.admin_notes && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                  Admin Notes
                </h2>
                <div className="whitespace-pre-wrap">{report.admin_notes}</div>
              </div>
            )}

            <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-hpi-dark-gray font-semibold">
                    Entries ({filteredEntries.length})
                  </h2>
                  {isPrepared && (
                    <div className="border-hpi-light-gray flex rounded border text-sm">
                      <button
                        onClick={() => setViewMode("original")}
                        className={`px-3 py-1 ${viewMode === "original" ? "bg-hpi-dark-blue text-white" : "text-hpi-dark-gray hover:bg-hpi-pale-blue"}`}
                      >
                        Original
                      </button>
                      <button
                        onClick={() => setViewMode("final")}
                        className={`border-hpi-light-gray border-l px-3 py-1 ${viewMode === "final" ? "bg-hpi-dark-blue text-white" : "text-hpi-dark-gray hover:bg-hpi-pale-blue"}`}
                      >
                        Final
                      </button>
                      <button
                        onClick={() => setViewMode("all")}
                        className={`border-hpi-light-gray border-l px-3 py-1 ${viewMode === "all" ? "bg-hpi-dark-blue text-white" : "text-hpi-dark-gray hover:bg-hpi-pale-blue"}`}
                      >
                        All Revisions
                      </button>
                    </div>
                  )}
                </div>
                {report.can_add_entries && (
                  <Link
                    href={`${basePath}/${report.id}/entries/new`}
                    className="text-hpi-active-blue hover:text-hpi-active-blue text-sm"
                  >
                    Add Entry
                  </Link>
                )}
              </div>
              {filteredEntries.length > 0 ? (
                <div className="space-y-3">
                  {filteredEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="border-hpi-light-gray border-l-2 pl-3"
                    >
                      <div className="text-hpi-med-gray flex items-center gap-2 text-sm">
                        <span>{formatDateTime(entry.start_time)}</span>
                        <span>-</span>
                        <span>{entry.user_name}</span>
                        {entry.has_photo && (
                          <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
                            P
                          </span>
                        )}
                        {entry.has_video && (
                          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-700">
                            V
                          </span>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">
                        {entry.entry_text}
                      </div>
                      {(entry.also_names.length > 0 ||
                        canToggleAlso(entry)) && (
                        <div className="text-hpi-med-gray mt-1 flex items-center gap-2 text-sm">
                          <span className="font-medium">Also observed by:</span>
                          {entry.also_names.length > 0 && (
                            <span>{entry.also_names.join(", ")}</span>
                          )}
                          {canToggleAlso(entry) && (
                            <label className="inline-flex cursor-pointer items-center gap-1">
                              <input
                                type="checkbox"
                                checked={entry.also_user_ids.includes(
                                  report.current_user_id!,
                                )}
                                onChange={() => handleToggleAlso(entry.id)}
                                className="border-hpi-light-gray rounded"
                              />
                              <span>Me</span>
                            </label>
                          )}
                        </div>
                      )}
                      <div className="mt-1 flex gap-2">
                        <Link
                          href={`${basePath}/${report.id}/entries/${entry.id}`}
                          className="text-hpi-active-blue text-xs"
                        >
                          View
                        </Link>
                        {report.can_add_entries && (
                          <>
                            <Link
                              href={`${basePath}/${report.id}/entries/${entry.id}/edit`}
                              className="text-hpi-active-blue cursor-pointer text-xs hover:underline"
                            >
                              Edit
                            </Link>
                            {entry.entry_type === ENTRY_TYPE_ORIGINAL && (
                              <Link
                                href={`${basePath}/${report.id}/entries/${entry.id}/revise`}
                                className="text-hpi-active-blue cursor-pointer text-xs hover:underline"
                              >
                                Add Revision
                              </Link>
                            )}
                          </>
                        )}
                        {entry.can_delete && (
                          <button
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this entry?",
                                )
                              ) {
                                router.delete(
                                  `${basePath}/${report.id}/entries/${entry.id}`,
                                )
                              }
                            }}
                            className="text-hpi-red cursor-pointer text-xs hover:underline"
                          >
                            Delete
                          </button>
                        )}
                        {entry.entry_type === ENTRY_TYPE_REVISION && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                            Revision
                          </span>
                        )}
                        {entry.entry_type === ENTRY_TYPE_FINAL && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                            Final
                          </span>
                        )}
                      </div>
                      {viewMode !== "final" &&
                        filterNestedRevisions(entry.revisions).length > 0 && (
                          <div className="mt-2 ml-4 space-y-2">
                            {filterNestedRevisions(entry.revisions).map(
                              (rev) => (
                                <div
                                  key={rev.id}
                                  className={`border-l-2 pl-3 ${rev.entry_type === ENTRY_TYPE_FINAL ? "border-blue-300" : "border-amber-300"}`}
                                >
                                  <div className="text-hpi-med-gray flex items-center gap-2 text-xs">
                                    {rev.entry_type === ENTRY_TYPE_FINAL ? (
                                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-700">
                                        Final
                                      </span>
                                    ) : (
                                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">
                                        Revision
                                      </span>
                                    )}
                                    <span>{rev.user_name}</span>
                                    <span>
                                      {formatDateTime(rev.created_at)}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-sm whitespace-pre-wrap">
                                    {rev.entry_text}
                                  </div>
                                  {(report.can_add_entries ||
                                    rev.can_delete) && (
                                    <div className="mt-1 flex gap-2">
                                      {report.can_add_entries && (
                                        <Link
                                          href={`${basePath}/${report.id}/entries/${rev.id}/edit`}
                                          className="text-hpi-active-blue cursor-pointer text-xs hover:underline"
                                        >
                                          Edit Revision
                                        </Link>
                                      )}
                                      {rev.can_delete && (
                                        <button
                                          onClick={() => {
                                            if (
                                              confirm(
                                                "Are you sure you want to delete this revision?",
                                              )
                                            ) {
                                              router.delete(
                                                `${basePath}/${report.id}/entries/${rev.id}`,
                                              )
                                            }
                                          }}
                                          className="text-hpi-red cursor-pointer text-xs hover:underline"
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-hpi-med-gray text-sm">No entries yet.</p>
              )}
            </div>

            {report.rejected_notes && (
              <div className="border-hpi-red/30 bg-hpi-red/5 mb-6 rounded border p-4">
                <h2 className="text-hpi-red mb-2 font-semibold">
                  Rejection Notes
                </h2>
                <div className="whitespace-pre-wrap">
                  {report.rejected_notes}
                </div>
              </div>
            )}

            {report.rejections.length > 0 && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-3 font-semibold">
                  Rejection History ({report.rejections.length})
                </h2>
                <div className="space-y-3">
                  {report.rejections.map((r) => (
                    <div
                      key={r.id}
                      className="border-hpi-red/30 border-l-2 pl-3"
                    >
                      <div className="text-hpi-med-gray text-sm">
                        {r.rejected_by_name} - {formatDateTime(r.rejected_at)}
                      </div>
                      <div className="whitespace-pre-wrap">{r.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.log && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">Log</h2>
                <div className="text-sm whitespace-pre-wrap">{report.log}</div>
              </div>
            )}
          </div>

          <div>
            <div className="border-hpi-light-gray rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-3 font-semibold">Details</h2>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-hpi-med-gray">Category</dt>
                  <dd>{CATEGORY_LABELS[report.category] || "Unknown"}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Report Date</dt>
                  <dd>{formatDate(report.report_date)}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Team Leader</dt>
                  <dd>{report.investigator_name ?? "-"}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Author</dt>
                  <dd>{report.user_name ?? "-"}</dd>
                </div>
                {report.completed_by_name && (
                  <div>
                    <dt className="text-hpi-med-gray">Finalized</dt>
                    <dd>
                      {formatDateTime(report.completed_at)} by{" "}
                      {report.completed_by_name}
                    </dd>
                  </div>
                )}
                {report.approved_by_name && (
                  <div>
                    <dt className="text-hpi-med-gray">Approved</dt>
                    <dd>
                      {formatDateTime(report.approved_at)} by{" "}
                      {report.approved_by_name}
                    </dd>
                  </div>
                )}
                {report.prepared_by_name && (
                  <div>
                    <dt className="text-hpi-med-gray">Prepared</dt>
                    <dd>
                      {formatDateTime(report.prepared_at)} by{" "}
                      {report.prepared_by_name}
                    </dd>
                  </div>
                )}
                {report.reviewed_by_name && (
                  <div>
                    <dt className="text-hpi-med-gray">Reviewed</dt>
                    <dd>
                      {formatDateTime(report.reviewed_at)} by{" "}
                      {report.reviewed_by_name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {report.approved_text && (
              <div className="border-hpi-dark-green/30 bg-hpi-dark-green/5 mt-4 rounded border p-4">
                <h2 className="text-hpi-dark-green mb-2 font-semibold">
                  Approval Notes
                </h2>
                <div className="text-sm whitespace-pre-wrap">
                  {report.approved_text}
                </div>
              </div>
            )}

            {/* GAP 5 & 6: Enhanced Members panel with approval status and reject buttons */}
            {report.members.length > 0 && (
              <div className="border-hpi-light-gray mt-4 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-3 font-semibold">
                  Members ({report.members.length})
                </h2>
                <ul className="space-y-2 text-sm">
                  {report.members.map((m) => (
                    <li key={m.id}>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          {m.user_name}
                          {m.approved_at ? (
                            <span className="text-hpi-dark-green inline-flex items-center gap-1 text-xs">
                              <Check className="h-3 w-3" />
                              Approved {formatDate(m.approved_at)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                              <Clock className="h-3 w-3" />
                              Pending
                            </span>
                          )}
                        </span>
                        {/* GAP 6: Per-member reject button */}
                        {report.can_reject_member && m.approved_at && (
                          <button
                            onClick={() => {
                              setMemberRejectId(
                                memberRejectId === m.id ? null : m.id,
                              )
                              setMemberRejectReason("")
                            }}
                            className="text-hpi-red hover:text-hpi-red/80 text-xs"
                            title="Reject this member's entries"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {/* Inline reject reason form */}
                      {memberRejectId === m.id && (
                        <div className="border-hpi-red/20 bg-hpi-red/5 mt-2 rounded border p-2">
                          <textarea
                            value={memberRejectReason}
                            onChange={(e) =>
                              setMemberRejectReason(e.target.value)
                            }
                            className="border-hpi-light-gray mb-2 w-full rounded border px-2 py-1 text-xs"
                            rows={2}
                            placeholder="Reason for rejection..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRejectMember(m.id)}
                              disabled={!memberRejectReason.trim()}
                              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-2 py-1 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => setMemberRejectId(null)}
                              className="text-hpi-med-gray text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}/reports`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Reports
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
